import { Box, CircularProgress, Stack, Theme } from '@mui/material';
import {
  DisplayErrors,
  REQUEST_CHOOSE_LIMIT_NOTE,
  RequestChooseScreen,
  type RequestChooseScreenHeldType,
  type TEntityChoice,
} from '@verifime/components';
import { SERVER_ERRORS } from '@verifime/utils';
import { useMemo, useState } from 'react';
import { useRequestChooseReads } from '../hooks/useRequestChooseReads';
import type { TRequestCriteria } from '../requestCriteria';
import {
  entityTypeDescription,
  entityTypeIcon,
  entityTypeTitle,
  newIdentityName,
} from './requestChooseContent';
import {
  buildRequestChooseModel,
  type TRequestChooseChoice,
  type TRequestChooseHeldType,
  type TRequestChooseIdentity,
} from './requestChooseModel';

/**
 * What Continue hands to the caller: the entity type, and the id of the identity chosen. A new
 * one has no identity yet, so it carries the entity type alone.
 */
export type TRequestChooseSelection = { choice: TEntityChoice; identityId?: string };

export type RequestChooseViewProps = {
  /** Who is asking. */
  providerName: string;
  /** What the request asks for, as the customer should read it. */
  asksLabel: string;
  /** What the request permits. Only identities that fit it show. */
  criteria: TRequestCriteria;
  /**
   * True to offer "just me", the Individual tile, to a customer who holds an Individual (verifime-adr#709
   * Q1). The caller decides it from the invite; this view only displays it, and never preselects it.
   * Continue then calls `onContinue({ choice: 'individual' })`.
   */
  offersJustMe: boolean;
  /** The client that asks. An identity already shared with this client cannot be chosen. */
  organisationId: string;
  /** Called on Continue with the choice. Nothing is saved or shared here (618 L5). */
  onContinue: (selection: TRequestChooseSelection) => void;
  onCancel: () => void;
};

function toRowProps({
  entityType,
  identities,
}: TRequestChooseHeldType): RequestChooseScreenHeldType {
  return {
    entityType,
    typeLabel: entityTypeTitle(entityType),
    description: entityTypeDescription(entityType),
    icon: entityTypeIcon(entityType),
    identities,
  };
}

/**
 * The full name of the chosen option, shown by the action: the identity's, "A new ..." for a new
 * one, or the Individual tile's own title for just me.
 */
function nameOfChoice({ entityType, identity }: TRequestChooseChoice): string {
  if (identity) {
    return identity.name;
  }
  return entityType === 'individual' ? entityTypeTitle(entityType) : newIdentityName(entityType);
}

/** What Continue hands over for the chosen option. */
const selectionOf = ({ entityType, identity }: TRequestChooseChoice): TRequestChooseSelection =>
  identity ? { choice: entityType, identityId: identity.id } : { choice: entityType };

type TLoadedProps = Omit<RequestChooseViewProps, 'organisationId'> & {
  identities: TRequestChooseIdentity[];
  sharedIdentityIds: ReadonlySet<string>;
};

/** The screen once the reads are in: the customer's choice is its state. */
function LoadedRequestChoose({
  providerName,
  asksLabel,
  criteria,
  offersJustMe,
  identities,
  sharedIdentityIds,
  onContinue,
  onCancel,
}: Readonly<TLoadedProps>) {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const model = useMemo(
    () =>
      buildRequestChooseModel({ criteria, identities, sharedIdentityIds, offersJustMe, chosenId }),
    [criteria, identities, sharedIdentityIds, offersJustMe, chosenId],
  );

  const handlePrimary = () => {
    // The primary is disabled while nothing is chosen, so this only guards the type.
    if (model.selected) {
      onContinue(selectionOf(model.selected));
    }
  };

  return (
    <RequestChooseScreen
      data-testid="request-choose-view"
      providerName={providerName}
      asksLabel={asksLabel}
      heldTypes={model.heldTypes.map(toRowProps)}
      unheldTypes={model.unheldTypes}
      offersJustMe={model.offersJustMe}
      selectedId={model.selectedId}
      selectedName={model.selected ? nameOfChoice(model.selected) : undefined}
      primaryAction={model.primaryAction}
      limitNote={model.limitsChoice ? REQUEST_CHOOSE_LIMIT_NOTE : undefined}
      onSelect={setChosenId}
      onPrimary={handlePrimary}
      onCancel={onCancel}
    />
  );
}

/**
 * The request choose screen for one request: what the customer already holds that fits, first, and
 * the types they hold none of as new ones. It reads the customer's identities
 * (`GET /v1/customer/me`) and which of them are already shared with the client
 * (`GET /v1/permission?customerId=`), works out what arrives chosen, and composes
 * `RequestChooseScreen`. Continue hands the choice to the caller (a Draft, an identity or a new
 * one all go on to the confirm screen); nothing is shared here. ADR 62: the view owns the reads
 * and the rules; navigation is the caller's, via the callbacks.
 */
export default function RequestChooseView({
  organisationId,
  ...request
}: Readonly<RequestChooseViewProps>) {
  const reads = useRequestChooseReads(request.criteria, organisationId);

  if (reads.status === 'error') {
    return (
      <Box sx={(theme: Theme) => ({ padding: theme.semantic.spacing.lg })}>
        <DisplayErrors errors={[{ message: SERVER_ERRORS.default }]} />
      </Box>
    );
  }
  if (reads.status === 'loading') {
    return (
      <Stack alignItems="center" sx={(theme: Theme) => ({ padding: theme.semantic.spacing.lg })}>
        <CircularProgress />
      </Stack>
    );
  }
  return (
    <LoadedRequestChoose
      {...request}
      identities={reads.identities}
      sharedIdentityIds={reads.sharedIdentityIds}
    />
  );
}
